import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  MessageSquare,
  Calendar,
  Send,
  ArrowRight,
  Building2,
  Clock,
} from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { API_ROOT } from "../../utils/apiConfig";

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

/* ─── CONTACT INFO ─── */
const contactMethods = [
  {
    icon: MessageSquare,
    title: "WhatsApp",
    desc: "Chat with us instantly — the fastest way to get a response.",
    action: "Chat Now",
    href: "https://wa.me/917004771388?text=Hi%20BusiGrow%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20platform.",
    iconColor: "text-green-600",
    bgColor: "bg-green-50",
  },
  {
    icon: Mail,
    title: "Email",
    desc: "For detailed inquiries, partnerships, or enterprise questions.",
    action: "grow@busigrow.in",
    href: "mailto:grow@busigrow.in",
    iconColor: "text-violet-600",
    bgColor: "bg-violet-50",
  },
  {
    icon: Phone,
    title: "Phone",
    desc: "Prefer to talk? We're available during Indian business hours.",
    action: "+91 7004 771 388",
    href: "tel:+917004771388",
    iconColor: "text-blue-600",
    bgColor: "bg-blue-50",
  },
  {
    icon: Calendar,
    title: "Book a Demo",
    desc: "Schedule a 30-minute call to see BusiGrow in action.",
    action: "Book on Calendly",
    href: "https://calendly.com/tanzeelkhan78687/30min",
    iconColor: "text-purple-600",
    bgColor: "bg-purple-50",
  },
];

/* ─── HERO ─── */
const ContactHero = () => (
  <section className="relative pt-24 sm:pt-32 pb-12 sm:pb-16 overflow-hidden hero-mesh">
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-violet-300/15 rounded-full blur-[120px]" />
    </div>
    {/* Decorative floating orbs */}
    <div className="absolute top-10 right-10 w-16 h-16 rounded-full bg-violet-200/40 animate-float-orb" />
    <div
      className="absolute bottom-10 left-10 w-10 h-10 rounded-full bg-emerald-200/40 animate-float-orb"
      style={{ animationDelay: "2s" }}
    />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        {/* Left — text content */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="text-center lg:text-left"
        >
          <motion.h1
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 leading-tight"
          >
            Let's build something{" "}
            <span className="text-gradient">extraordinary</span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 sm:mt-6 text-base sm:text-lg text-slate-500 max-w-xl mx-auto lg:mx-0"
          >
            Whether you're exploring automation, need a demo, or want to discuss
            a custom project — we'd love to hear from you.
          </motion.p>
        </motion.div>

        {/* Right — hero image (hidden on mobile) */}
        <motion.div
          initial={{ opacity: 0, x: 60, filter: "blur(8px)" }}
          animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="hidden lg:block relative"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-violet-200/30 animate-morph-blob" />
          <img
            src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?auto=format&fit=crop&w=800&q=80"
            alt="Phone and communication"
            className="relative rounded-3xl shadow-apple-xl card-shine img-parallax w-full object-cover"
            loading="lazy"
          />
          {/* Floating 24hr response card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="absolute -bottom-4 -left-6 glass-strong rounded-xl px-4 py-3 shadow-apple-lg flex items-center gap-2"
          >
            <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900">24hr Response</p>
              <p className="text-xs text-slate-500">Guaranteed</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── CONTACT METHODS ─── */
const ContactMethods = () => (
  <section className="relative py-10 sm:py-16">
    {/* Decorative floating orbs */}
    <div className="absolute top-10 right-10 w-16 h-16 rounded-full bg-violet-200/40 animate-float-orb" />
    <div
      className="absolute bottom-10 left-10 w-10 h-10 rounded-full bg-emerald-200/40 animate-float-orb"
      style={{ animationDelay: "2s" }}
    />

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
      >
        {contactMethods.map((m, i) => (
          <motion.a
            key={i}
            href={m.href}
            target={m.href.startsWith("http") ? "_blank" : undefined}
            rel={m.href.startsWith("http") ? "noopener noreferrer" : undefined}
            variants={fadeUp}
            custom={i}
            whileHover={{ y: -6, scale: 1.02 }}
            className="group p-6 rounded-2xl glass shadow-apple hover:shadow-apple-lg transition-all card-shine"
          >
            <div
              className={`w-12 h-12 rounded-xl ${m.bgColor} flex items-center justify-center`}
            >
              <m.icon className={`w-6 h-6 ${m.iconColor}`} />
            </div>
            <h3 className="mt-4 text-base font-bold text-slate-900">
              {m.title}
            </h3>
            <p className="mt-1 text-sm text-slate-500">{m.desc}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-violet-600 group-hover:gap-2 transition-all">
              {m.action} <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </motion.a>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ─── CONTACT FORM ─── */
const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_ROOT}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to submit form right now.");
      }

      setSubmitted(true);
    } catch (error) {
      setErrorMessage(error.message || "Unable to submit form right now.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <AnimatePresence>
        <motion.div
          key="success"
          initial={{ opacity: 0, scale: 0.9, filter: "blur(6px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          exit={{ opacity: 0, scale: 0.9, filter: "blur(6px)" }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="text-center py-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              delay: 0.15,
            }}
            className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4"
          >
            <svg
              className="w-8 h-8 text-emerald-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </motion.div>
          <h3 className="text-2xl font-bold text-slate-900">Message sent!</h3>
          <p className="mt-2 text-slate-500">
            Thanks! Your request has been received. We will contact you within
            24 hours.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: "",
                email: "",
                company: "",
                phone: "",
                interest: "",
                message: "",
              });
            }}
            className="mt-6 text-sm text-violet-600 font-medium hover:underline"
          >
            Send another message
          </button>
        </motion.div>
      </AnimatePresence>
    );
  }

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all text-sm shadow-sm";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="transition-all duration-300 focus-within:ring-2 focus-within:ring-violet-400 rounded-xl">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Full Name *
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={inputClass}
            placeholder="Your name"
          />
        </div>
        <div className="transition-all duration-300 focus-within:ring-2 focus-within:ring-violet-400 rounded-xl">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Email *
          </label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            className={inputClass}
            placeholder="you@company.com"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="transition-all duration-300 focus-within:ring-2 focus-within:ring-violet-400 rounded-xl">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Company
          </label>
          <input
            type="text"
            value={formData.company}
            onChange={(e) =>
              setFormData({ ...formData, company: e.target.value })
            }
            className={inputClass}
            placeholder="Your company name"
          />
        </div>
        <div className="transition-all duration-300 focus-within:ring-2 focus-within:ring-violet-400 rounded-xl">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            className={inputClass}
            placeholder="+91 98765 43210"
          />
        </div>
      </div>

      <div className="transition-all duration-300 focus-within:ring-2 focus-within:ring-violet-400 rounded-xl">
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          What are you interested in? *
        </label>
        <select
          required
          value={formData.interest}
          onChange={(e) =>
            setFormData({ ...formData, interest: e.target.value })
          }
          className={inputClass}
        >
          <option value="">Select an option</option>
          <option value="Branding & Identity">Branding &amp; Identity</option>
          <option value="Digital Marketing">Digital Marketing</option>
          <option value="Web & App Development">
            Web &amp; App Development
          </option>
          <option value="UI/UX Design">UI/UX Design</option>
          <option value="AI Automation">AI Automation</option>
          <option value="Content Creation">Content Creation</option>
          <option value="Full Service Package">Full Service Package</option>
          <option value="Enterprise Plan">Enterprise Plan</option>
          <option value="Partnership">Partnership / Reseller</option>
          <option value="Careers">Careers — Join the Team</option>
          <option value="Other">Something Else</option>
        </select>
      </div>

      <div className="transition-all duration-300 focus-within:ring-2 focus-within:ring-violet-400 rounded-xl">
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Message *
        </label>
        <textarea
          required
          rows={4}
          value={formData.message}
          onChange={(e) =>
            setFormData({ ...formData, message: e.target.value })
          }
          className={`${inputClass} resize-none`}
          placeholder="Tell us about your business and what you're looking for..."
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold hover:shadow-apple-lg hover:scale-[1.02] transition-all"
      >
        <Send className="w-4 h-4" />
        {isSubmitting ? "Sending..." : "Send Message"}
      </button>

      {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}

      <p className="text-xs text-slate-400">
        We'll respond via WhatsApp or email within 24 hours. Your info is never
        shared with third parties.
      </p>
    </form>
  );
};

/* ─── FORM SECTION ─── */
const FormSection = () => (
  <section className="relative py-16 sm:py-24 bg-violet-tint">
    {/* Decorative floating orbs */}
    <div className="absolute top-10 right-10 w-16 h-16 rounded-full bg-violet-200/40 animate-float-orb" />
    <div
      className="absolute bottom-10 left-10 w-10 h-10 rounded-full bg-emerald-200/40 animate-float-orb"
      style={{ animationDelay: "2s" }}
    />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10 sm:gap-16">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.h2
            variants={fadeUp}
            className="text-2xl sm:text-3xl font-bold text-slate-900"
          >
            Send us a message
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-2 text-slate-500 mb-8"
          >
            Fill out the form and we'll get back to you quickly.
          </motion.p>
          <div className="card-shine shadow-apple-xl rounded-3xl p-5 sm:p-8 glass-strong">
            <ContactForm />
          </div>

          {/* Office image */}
          <div className="relative rounded-2xl overflow-hidden shadow-apple mt-8">
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80"
              alt="Our Office"
              className="w-full h-48 object-cover img-parallax"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 glass-strong rounded-xl px-4 py-2">
              <p className="text-sm font-bold text-white">BusiGrow HQ</p>
              <p className="text-xs text-white/70">Mumbai, India</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-4 sm:space-y-6"
        >
          <div className="p-5 sm:p-6 rounded-2xl glass shadow-apple">
            <h3 className="text-base font-bold text-slate-900 mb-4">Office</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-700 font-medium">
                    India (HQ)
                  </p>
                  <p className="text-xs text-slate-400">Bangalore, India</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-700 font-medium">
                    Business Hours
                  </p>
                  <p className="text-xs text-slate-400">
                    Mon-Sat, 10:00 AM - 7:00 PM IST
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-700 font-medium">
                    Expanding to
                  </p>
                  <p className="text-xs text-slate-400">Dubai, UAE — Q2 2026</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-700 text-white shadow-apple-lg">
            <h3 className="text-base font-bold mb-3">Quick Actions</h3>
            <div className="space-y-2.5">
              <a
                href="https://wa.me/917004771388?text=Hi%20BusiGrow!"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
              >
                <MessageSquare className="w-4 h-4" /> Chat on WhatsApp
              </a>
              <a
                href="https://calendly.com/tanzeelkhan78687/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
              >
                <Calendar className="w-4 h-4" /> Book a 30-min Demo
              </a>
              <a
                href="mailto:grow@busigrow.in"
                className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" /> grow@busigrow.in
              </a>
              <a
                href="tel:+917004771388"
                className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4" /> +91 7004 771 388
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── PAGE ─── */
const ContactPage = () => (
  <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
    <Header />
    <ContactHero />
    <ContactMethods />
    <FormSection />
    <Footer />
  </div>
);

export default ContactPage;
